import React from 'react';
import { Box, Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Drawer, Button, IconButton } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TableBarIcon from '@mui/icons-material/TableBar';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import InventoryIcon from '@mui/icons-material/Inventory';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SettingsIcon from '@mui/icons-material/Settings';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import TimelineIcon from '@mui/icons-material/Timeline';
import TvIcon from '@mui/icons-material/Tv';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PeopleIcon from '@mui/icons-material/People';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import PrintIcon from '@mui/icons-material/Print';
import LockIcon from '@mui/icons-material/Lock';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CloseIcon from '@mui/icons-material/Close';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import versionInfo from '../config/version.json';

export default function Sidebar() {
    const {
        activeView,
        setActiveView,
        subscribedModules,
        currentRole,
        sidebarMobileOpen,
        setSidebarMobileOpen,
        currentUser,
        allowedMenus,
        logout,
        showToast,
        themeMode,
        setThemeMode
    } = useApp();
    const { t } = useLanguage();

    const viewToModuleMap = {
        dashboard: null,
        franchiseHq: null,
        tables: 'pos',
        pos: 'pos',
        quickPos: 'pos',
        kds: 'kds',
        captain: 'captain',
        inventory: 'inventory',
        accounting: 'accounting',
        reports: 'pos',
        settings: 'pos',
        ownerSettings: 'pos',
        rolePermissions: 'pos',
        subscriptions: null,
        liveStatus: null,
        tokenDisplay: null,
        qrOrdering: null,
        crm: null,
        cloudSaaS: null,
        utility: null
    };

    const allMenuItems = [
        { id: 'dashboard', label: t('nav-dash'), icon: <DashboardIcon /> },
        { id: 'franchiseHq', label: t('nav-franchise-hq'), icon: <BusinessIcon /> },
        { id: 'tables', label: t('nav-tables'), icon: <TableBarIcon /> },
        { id: 'pos', label: t('nav-pos'), icon: <PointOfSaleIcon /> },
        { id: 'quickPos', label: t('nav-quick-pos'), icon: <FlashOnIcon /> },
        { id: 'kds', label: t('nav-kds'), icon: <SoupKitchenIcon /> },
        { id: 'inventory', label: t('nav-inventory'), icon: <InventoryIcon /> },
        { id: 'accounting', label: t('nav-accounting'), icon: <AccountBalanceWalletIcon /> },
        { id: 'captain', label: t('nav-waiter-sim'), icon: <PhoneAndroidIcon /> },
        { id: 'biz', label: 'Dinexa Biz (Owner)', icon: <BusinessIcon /> },
        { id: 'reports', label: t('nav-reports'), icon: <AssessmentIcon /> },
        { id: 'liveStatus', label: t('nav-live-status'), icon: <TimelineIcon /> },
        { id: 'tokenDisplay', label: t('nav-token-display'), icon: <TvIcon /> },
        { id: 'qrOrdering', label: t('nav-qr-order'), icon: <QrCodeScannerIcon /> },
        { id: 'crm', label: t('nav-crm'), icon: <PeopleIcon /> },
        { id: 'cloudSaaS', label: t('nav-cloud-saas'), icon: <CloudQueueIcon /> },
        { id: 'printerSettings', label: t('nav-printer-settings'), icon: <PrintIcon /> },
        { id: 'utility', label: 'Utility', icon: <BuildIcon /> },
        { id: 'ownerSettings', label: t('nav-owner-settings'), icon: <SettingsIcon /> },
        { id: 'rolePermissions', label: t('nav-permissions') || 'Role Permissions', icon: <LockIcon /> },
        { id: 'subscriptions', label: t('nav-subscriptions'), icon: <SubscriptionsIcon /> },
        { id: 'developerDashboard', label: 'Developer Portal', icon: <SettingsIcon /> }
    ];

    const activeAllowed = (allowedMenus && Array.isArray(allowedMenus) && allowedMenus.length > 0) 
        ? allowedMenus 
        : (currentUser?.allowed_menus && Array.isArray(currentUser.allowed_menus) && currentUser.allowed_menus.length > 0 ? currentUser.allowed_menus : null);

    const menuItems = allMenuItems.filter(item => {
        // Developer portal item is strictly for developer role
        if (item.id === 'developerDashboard') {
            return currentRole === 'developer';
        }
        if (currentRole === 'developer') {
            return item.id === 'developerDashboard';
        }

        // System configuration items restricted to Owner
        if (item.id === 'rolePermissions' && currentRole !== 'owner') {
            return false;
        }
        if ((item.id === 'utility' || item.id === 'ownerSettings') && currentRole !== 'owner') {
            return false;
        }

        // 1. STRICT FILTER: If allowed_menus is configured for this tenant / branch / role, ONLY show enabled options!
        if (activeAllowed && Array.isArray(activeAllowed) && activeAllowed.length > 0) {
            return activeAllowed.includes(item.id);
        }

        // 2. If subscribedModules is configured, filter out unsubscribed operational modules
        if (subscribedModules && Array.isArray(subscribedModules) && subscribedModules.length > 0) {
            const requiredModule = viewToModuleMap[item.id];
            if (requiredModule && !subscribedModules.includes(requiredModule)) {
                return false;
            }
        }

        // 3. Fallback defaults by role when no custom permissions are set:
        if (currentRole === 'owner' || currentRole === 'admin') {
            return true;
        }

        if (currentRole === 'captain') {
            return ['captain', 'tables', 'pos'].includes(item.id);
        }

        if (currentRole === 'kitchen' || currentRole === 'kitchen_staff') {
            return ['kds'].includes(item.id);
        }

        if (currentRole === 'manager') {
            return item.id !== 'subscriptions' && item.id !== 'franchiseHq';
        }

        if (currentRole === 'staff' || currentRole === 'billing' || currentRole === 'billing_staff') {
            return ['tables', 'pos', 'quickPos', 'kds', 'captain', 'reports', 'liveStatus', 'tokenDisplay', 'qrOrdering', 'printerSettings'].includes(item.id);
        }

        return true;
    });

    const drawerContent = () => {
        return (
            <Box sx={{
                height: '100%',
                bgcolor: '#008751', // Dinexa Forest Green
                color: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                borderRight: 'none'
            }}>
                {/* Logo & Header area with explicit close button */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 65,
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box 
                            component="img" 
                            src={process.env.PUBLIC_URL + '/logo192.png'} 
                            alt="DinexaPOS Logo" 
                            sx={{ 
                                width: 34, 
                                height: 34, 
                                borderRadius: 1.5, 
                                objectFit: 'cover',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                            }} 
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: 0.3, lineHeight: 1.1, color: '#f8fafc', fontSize: '0.95rem' }}>
                                {currentRole === 'developer' ? 'Developer' : (currentUser?.tenant_name || 'DinexaPOS')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: currentRole === 'developer' ? '#a78bfa' : '#a7f3d0', fontWeight: 800, fontSize: '0.68rem', display: 'block', mt: 0.2 }}>
                                {currentUser?.branch_name ? `${currentUser.branch_name}` : (currentRole === 'developer' ? 'ADMIN PORTAL' : 'SMART POS PLATFORM')}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={() => {
                            if (typeof setSidebarMobileOpen === 'function') setSidebarMobileOpen(false);
                        }}
                        sx={{ 
                            color: '#ffffff', 
                            bgcolor: 'rgba(255,255,255,0.1)', 
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                            p: 0.8
                        }}
                        size="small"
                        aria-label="Close drawer"
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* Navigation links */}
                <List sx={{
                    px: 1.5,
                    py: 1,
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.4,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '5px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        bgcolor: 'rgba(255,255,255,0.2)',
                        borderRadius: '3px'
                    }
                }}>
                    {menuItems.map((item) => {
                        const isActive = activeView === item.id;

                        return (
                            <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
                                <ListItemButton
                                    onClick={() => {
                                        setActiveView(item.id);
                                        if (typeof setSidebarMobileOpen === 'function') {
                                            setSidebarMobileOpen(false);
                                        }
                                    }}
                                    sx={{
                                        borderRadius: 2.5,
                                        py: 0.9,
                                        px: 2,
                                        bgcolor: isActive ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
                                        color: isActive ? '#ffffff' : '#d1e7dd',
                                        borderLeft: isActive ? '4px solid #10b981' : '4px solid transparent',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            color: '#ffffff'
                                        },
                                        transition: 'all 0.15s ease-in-out'
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        color: 'inherit',
                                        minWidth: 38,
                                        justifyContent: 'center',
                                        '& svg': { fontSize: 22 }
                                    }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontWeight: isActive ? 900 : 600,
                                            fontSize: '0.88rem'
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Theme Switcher */}
                <Box sx={{ p: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#a7f3d0', fontWeight: 800, mb: 0.8, display: 'block', px: 0.5, fontSize: '0.68rem' }}>
                        THEME MODE
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                            variant="contained"
                            size="small"
                            onClick={() => setThemeMode('light')}
                            sx={{ 
                                flex: 1, 
                                bgcolor: themeMode === 'light' ? '#ffffff' : 'rgba(255,255,255,0.1)', 
                                color: themeMode === 'light' ? '#0f172a' : '#d1e7dd',
                                '&:hover': { bgcolor: '#f8fafc' },
                                fontSize: '0.7rem',
                                fontWeight: 800
                            }}
                        >
                            Light
                        </Button>
                        <Button 
                            variant="contained"
                            size="small"
                            onClick={() => setThemeMode('solid')}
                            sx={{ 
                                flex: 1, 
                                bgcolor: themeMode === 'solid' ? '#0d9488' : 'rgba(255,255,255,0.1)', 
                                color: themeMode === 'solid' ? '#ffffff' : '#d1e7dd',
                                '&:hover': { bgcolor: '#0f766e' },
                                fontSize: '0.7rem',
                                fontWeight: 800
                            }}
                        >
                            Solid
                        </Button>
                        <Button 
                            variant="contained"
                            size="small"
                            onClick={() => setThemeMode('dark')}
                            sx={{ 
                                flex: 1, 
                                bgcolor: themeMode === 'dark' ? '#1e293b' : 'rgba(255,255,255,0.1)', 
                                color: themeMode === 'dark' ? '#f8fafc' : '#d1e7dd',
                                '&:hover': { bgcolor: '#334155' },
                                fontSize: '0.7rem',
                                fontWeight: 800,
                                border: '1px solid',
                                borderColor: themeMode === 'dark' ? '#475569' : 'transparent'
                            }}
                        >
                            Dark
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Check Updates & Sign Out Action Buttons */}
                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Check Updates Button */}
                    <ListItemButton
                        onClick={() => {
                            if (showToast) showToast('🔄 नवीन व्हर्जन तपासत आहे...');
                            if ('serviceWorker' in navigator) {
                                navigator.serviceWorker.getRegistrations().then(registrations => {
                                    for (let registration of registrations) {
                                        registration.update();
                                    }
                                });
                            }
                            setTimeout(() => {
                                window.location.reload(true);
                            }, 800);
                        }}
                        sx={{
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.08)',
                            color: '#a7f3d0',
                            py: 0.8,
                            px: 1.5,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' }
                        }}
                    >
                        <ListItemIcon sx={{ color: '#10b981', minWidth: 32 }}>
                            <AutorenewIcon sx={{ fontSize: 18 }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('check-updates')}
                            primaryTypographyProps={{ fontWeight: 800, fontSize: '0.8rem', color: '#ffffff' }}
                        />
                    </ListItemButton>

                    {/* Sign Out Button */}
                    <ListItemButton
                        onClick={() => {
                            if (typeof setSidebarMobileOpen === 'function') setSidebarMobileOpen(false);
                            logout();
                        }}
                        sx={{
                            borderRadius: 2,
                            bgcolor: 'rgba(225,29,72,0.18)',
                            color: '#fecdd3',
                            py: 0.8,
                            px: 1.5,
                            border: '1px solid rgba(225,29,72,0.35)',
                            '&:hover': { bgcolor: 'rgba(225,29,72,0.3)' }
                        }}
                    >
                        <ListItemIcon sx={{ color: '#fb7185', minWidth: 32 }}>
                            <ExitToAppIcon sx={{ fontSize: 18 }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('sign-out')}
                            primaryTypographyProps={{ fontWeight: 800, fontSize: '0.8rem', color: '#ffffff' }}
                        />
                    </ListItemButton>
                </Box>

                {/* Footer status */}
                <Box sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.15)' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                        <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.74rem', fontWeight: 800 }}>
                            v{versionInfo.version}.{versionInfo.buildNumber} ({versionInfo.buildTime})
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', fontWeight: 600 }}>
                            Powered by e.Sense InfoSolutions 9921946393
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Drawer
            variant="temporary"
            anchor="left"
            open={Boolean(sidebarMobileOpen)}
            onClose={() => {
                if (typeof setSidebarMobileOpen === 'function') {
                    setSidebarMobileOpen(false);
                }
            }}
            ModalProps={{ keepMounted: true }}
            sx={{
                zIndex: 1400,
                '& .MuiDrawer-paper': { 
                    boxSizing: 'border-box', 
                    width: 280,
                    boxShadow: '4px 0 25px rgba(0,0,0,0.4)',
                    borderRight: 'none'
                }
            }}
        >
            {drawerContent()}
        </Drawer>
    );
}
